import {css, StyleSheet} from 'aphrodite';
import classNames from 'classnames';
import React, {useRef, useState} from 'react';

import {pluralize} from 'BenchlingUtil';
import {Selection} from 'molbio/maps/selectables/Selection';
import * as sequtil from 'molbio/SeqUtil';
import {FONT_FAMILY_PROXIMA, FONT_PROXIMA_11, FONT_WEIGHT_PROXIMA_SEMIBOLD} from 'styles/FontConstants';
import {ZINDEX_INPUT_BUBBLE} from 'styles/StyleConstants';
import getTestProps from 'test/lib/cypress/getTestProps';
import {GRAY, GRAY_DARK, GRAY_LIGHT, GRAY_LIGHTER, RED_LIGHTER} from 'ui-library/Colors';
import BenchlingIcon from 'ui-library/icons/BenchlingIcon';
import * as keyboard from 'util/Keyboard';

export const BUBBLE_WIDTH = 285;

export enum INPUT_BUBBLE_OPERATION {
  DELETE = 'delete',
  REPLACE = 'replace',
  INSERT = 'insert',
  CUT = 'cut',
}

const operationText = {
  [INPUT_BUBBLE_OPERATION.INSERT]: ' to insert ',
  [INPUT_BUBBLE_OPERATION.REPLACE]: ' to replace with ',
  [INPUT_BUBBLE_OPERATION.DELETE]: ' to delete ',
  [INPUT_BUBBLE_OPERATION.CUT]: ' to cut ',
};

interface SequenceFeatureCountsType {
  annotation?: number;
  'modified nucleotide'?: number;
  translation?: number;
  primer?: number;
  part?: number;
}

export interface InputBubbleProps {
  selection: Selection;
  pastedBases: string;
  featureCounts: SequenceFeatureCountsType | null;
  operation: INPUT_BUBBLE_OPERATION;
  top: number;
  left: number;
  offset: number;
  isPositionedAbove: boolean;
  sanitizeResidues: (residues: string) => string;
  onRequestClose: () => void;
  onReplaceOrDeleteResidues: (residues: string, start: number, end: number) => void;
  onInsertResidues: (residues: string, position: number) => void;
  onPaste?: () => void;
  residueName: string;
  sequenceStartIndex: number;
  onCheckNextResidueValid: (nextResidue: string) => boolean;
}

export function InputBubble({
  selection,
  pastedBases,
  featureCounts,
  operation,
  top,
  left,
  offset,
  isPositionedAbove,
  sanitizeResidues,
  onRequestClose,
  onReplaceOrDeleteResidues,
  onInsertResidues,
  onPaste,
  residueName,
  sequenceStartIndex,
  onCheckNextResidueValid,
}: InputBubbleProps): JSX.Element {
  const [residues, setResidues] = useState(sanitizeResidues(pastedBases));
  const numResidues =
    operation === INPUT_BUBBLE_OPERATION.DELETE || operation === INPUT_BUBBLE_OPERATION.CUT
      ? selection.length()
      : residues.length;
  const [isInputValid, setInputValid] = useState(true);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);

  function handleCommitResidues(): void {
    if (pastedBases && onPaste) {
      onPaste();
    } else if (selection.isNotEmpty()) {
      onReplaceOrDeleteResidues(residues, selection.start(), selection.end());
    } else {
      onInsertResidues(residues, selection.start());
    }
  }

  function handleKeyDown(e: React.KeyboardEvent<HTMLDivElement>): void {
    if (keyboard.isControlKey(e)) {
      // Keep control/meta for ourselves, so that parent map doesn't hear about it.
      e.stopPropagation();
      return;
    }
    // ARROWS, END, HOME
    if (keyboard.isAnArrowKey(e) || [35, 36].includes(e.which)) {
      e.stopPropagation();
      return;
    }
    if (keyboard.isADeleteKey(e)) {
      e.stopPropagation();
      return;
    }
    if (keyboard.isEscapeKey(e)) {
      onRequestClose();
      return;
    }
    if (keyboard.isEnterKey(e)) {
      handleCommitResidues();
      return;
    }
    const isNextCharValid = onCheckNextResidueValid(e.key);
    setInputValid(isNextCharValid);
    if (!isNextCharValid && !(e.ctrlKey || e.metaKey)) {
      // Only prevent default if it's going to type something.
      e.preventDefault();
    }
  }

  function handleKeyUp(e: React.KeyboardEvent<HTMLDivElement>): void {
    // We should never allow invalid residues to actually make it into the input due to the `sanitizeResidues`
    // call below, so the error state should only be shown transiently during a keyboard press.
    setInputValid(true);
  }

  return (
    <div
      style={{
        top,
        left,
        position: 'absolute',
      }}
    >
      <div
        className={classNames('input-bubble', css(styles.inputBubble))}
        onKeyDown={handleKeyDown}
        onKeyUp={handleKeyUp}
        tabIndex={-1}
        style={{...(isPositionedAbove ? {bottom: 0} : {}), width: BUBBLE_WIDTH}}
        {...getTestProps({component: 'InputBubble', element: 'root'})}
        onBlur={() => {
          if (!timeoutRef || !timeoutRef.current) {
            timeoutRef.current = setTimeout(() => {
              timeoutRef.current = null;
              onRequestClose();
            }, 1);
          }
        }}
        onFocus={() => {
          if (timeoutRef?.current != null) {
            clearTimeout(timeoutRef.current);
            timeoutRef.current = null;
          }
        }}
      >
        {!isPositionedAbove && <div className={css(styles.triangle)} style={{left: offset}} />}
        {![INPUT_BUBBLE_OPERATION.DELETE, INPUT_BUBBLE_OPERATION.CUT].includes(operation) && (
          <input
            type="text"
            className={classNames(css(styles.input), 'mono', {[css(styles.invalidInput)]: !isInputValid})}
            spellCheck={false}
            value={residues}
            onChange={(e) => {
              setResidues(sanitizeResidues(e.target.value));
            }}
            disabled={featureCounts != null}
            autoFocus={true}
          />
        )}
        {featureCounts != null && (
          <div className={classNames('paste-info', css(styles.pasteInfo))}>
            <span className={css(styles.pasteInfoIcon)}>
              <BenchlingIcon icon="info-circle" />
            </span>
            <p className={css(styles.inputBubbleParagraph)}>
              You're about to paste a sequence that includes{' '}
              {Object.entries(featureCounts).map(([feature, count], idx) => (
                <span key={feature}>{`${count} ${pluralize(count, feature)}${
                  idx === Object.keys(featureCounts).length - 1 ? '.' : ', '
                }`}</span>
              ))}
            </p>
          </div>
        )}
        <p className={classNames(css(styles.inputBubbleParagraph), css(styles.pressText))}>Press</p>
        <p className={css(styles.inputBubbleParagraph)}>
          <a {...getTestProps({component: 'InputBubble', element: 'enterButton'})}>
            <kbd className={css(styles.inputBubbleKeyboard)} onClick={handleCommitResidues}>
              ENTER
            </kbd>
          </a>
          {operationText[operation]}
          <span>{`${numResidues} ${pluralize(numResidues, residueName)}`}</span> at position
          {` ${sequtil.modelToDisplay(selection.start(), sequenceStartIndex)}`}.
        </p>
        <p className={css(styles.inputBubbleParagraph)}>
          <a>
            <kbd className={css(styles.inputBubbleKeyboard)} onClick={onRequestClose}>
              ESC
            </kbd>
          </a>{' '}
          to cancel.
        </p>
        {isPositionedAbove && (
          <div
            className={classNames(css(styles.triangle), css(styles.triangleBottom))}
            style={{left: offset}}
          />
        )}
      </div>
    </div>
  );
}

const TRIANGLE_WIDTH = 10;

const styles = StyleSheet.create({
  inputBubble: {
    outline: 'none',
    position: 'absolute',
    background: 'white',
    padding: '8px',
    border: `1px solid ${GRAY}`,
    borderRadius: '4px',
    marginLeft: `${-(TRIANGLE_WIDTH + 4)}px`,
    marginTop: `${-TRIANGLE_WIDTH}px`,
    zIndex: ZINDEX_INPUT_BUBBLE,
    boxShadow: `0 0 4px 0 ${GRAY_DARK}`,
  },
  inputBubbleParagraph: {
    margin: '5px 0 0',
    padding: '5px 0 3px',
    fontSize: '12px',
  },
  inputBubbleKeyboard: {
    backgroundColor: GRAY_LIGHT,
    fontFamily: FONT_FAMILY_PROXIMA,
    padding: '3px 4px',
    boxShadow: 'none',
    border: 'none',
    borderRadius: '4px',
    ':hover': {
      backgroundColor: GRAY,
    },
  },
  input: {
    marginBottom: '10px',
    outline: 0,
    boxShadow: 'none',
    fontSize: '1.2em',
    width: '100%',
  },
  triangle: {
    width: '0px',
    height: '0px',
    borderStyle: 'solid',
    borderWidth: `0 ${TRIANGLE_WIDTH}px ${TRIANGLE_WIDTH}px ${TRIANGLE_WIDTH}px`,
    borderColor: `transparent transparent ${GRAY} transparent`,
    position: 'absolute',
    marginTop: '-19px',
    marginLeft: '3px',
  },
  triangleBottom: {
    borderWidth: `${TRIANGLE_WIDTH}px ${TRIANGLE_WIDTH}px 0 ${TRIANGLE_WIDTH}px`,
    borderColor: `${GRAY} transparent transparent transparent`,
    marginTop: '9px',
  },
  invalidInput: {
    border: '2px solid',
    borderColor: RED_LIGHTER,
  },
  pasteInfo: {
    display: 'flex',
    background: GRAY_LIGHTER,
    padding: '0 8px 4px',
    margin: '8px 0',
  },
  pasteInfoIcon: {
    width: '28px',
    height: '28px',
    marginTop: '14px',
    marginRight: '6px',
  },
  pressText: {
    ...FONT_PROXIMA_11,
    fontWeight: FONT_WEIGHT_PROXIMA_SEMIBOLD,
    margin: '0',
    padding: '0',
  },
});
